/* import __COLOCATED_TEMPLATE__ from './asset-preview.hbs'; */
import { htmlSafe } from '@ember/template';
import { computed } from '@ember/object';
import { debounce } from '@ember/runloop';
import LSGModalBase from './lsg/lsg-modal-dialog-base';
import { showModal, closeModal } from "sh-www/utils/modal-controls";

/** The base for the template's CSS class. */
const CLASS_BASE = 'SH-c-asset-preview';

/** The amount of space to reserve on one side of the modal (percentage). */
const RESERVED_SPACE_PCT = .05;

/** The amount of extra space to reserve on both the top and bottom of the modal (pixels). */
const EXTRA_RESERVED_VERTICAL_SPACE_PX = 56;

/** The amount of padding to set on all sides of the modal (pixels). */
const PADDING_PX = 16;

/** The amount of time to let resize events accumulate before calculating layout (milliseconds). */
const WINDOW_RESIZE_DEBOUNCE_DELAY_MS = 100;

/**
 * The modal for previewing files.
 */
export default LSGModalBase.extend({
  propertyDefaults: {
    /**
     * @param {String} url The file's URL.
     */
    url: null,

    /**
     * @param {String} typeCategory The file's MIME type category.
     */
    typeCategory: null,

    /**
     * @param {Function} handleClickDownload The download button action.
     */
    handleClickDownload: () => (function() {}),
  },

  stateDefaults: {
    /**
     * @type {Boolean} Whether the asset is currently loading.
     */
    isLoading: false,

    /**
     * @type {Number} The asset's width (the default is arbitrary).
     */
    assetWidth: 192,

    /**
     * @type {Number} The asset's height (the default is arbitrary).
     */
    assetHeight: 108,
  },

  get destinationElement() {
    return document.getElementById('modal-overlays');
  },

  /**
   * The handler for the modal render complete event.
   */
  _onModalRenderComplete() {
    // Add listener for window resize events.
    this._boundResize = () => debounce(this, 'resizeWindow', WINDOW_RESIZE_DEBOUNCE_DELAY_MS);
    window.addEventListener('resize', this._boundResize, false);

    // Create the <img> or <video> element.
    this._createAssetElement();

    // Force this property to recalculate.
    this.notifyPropertyChange('_modalStyles');
  },

  /**
   * The handler for the modal close event.
   */
  _onClose() {
    // Remove listener for window resize events.
    window.removeEventListener('resize', this._boundResize, false);
    this._boundResize = null;
  },

  /**
   * The handler for the window resize event.
   */
  resizeWindow() {
    // Force this property to recalculate.
    this.notifyPropertyChange('_modalStyles');
  },

  /**
   * Gets the asset dimensions and stores them.
   */
  _createAssetElement() {
    const url = this.get('model.url');
    const typeCategory = this.get('model.typeCategory');
    const parentElement = document.querySelector('.sh-modal-container .modal');
    const createAssetElement = {
      image: this._createImageElement,
      video: this._createVideoElement,
    };

    this.set('state.isLoading', true);

    createAssetElement[typeCategory](url)
      .then((asset) => {
        // Get the asset dimensions.
        const width = asset[typeCategory === 'image' ? 'width' : 'videoWidth'];
        const height = asset[typeCategory === 'image' ? 'height' : 'videoHeight'];

        // Store the asset dimensions so that the modal can size itself accordingly.
        this.set('state.assetWidth', width);
        this.set('state.assetHeight', height);

        // Insert the asset element into the DOM.
        parentElement.appendChild(asset);
      })
      .catch(() => {
        showModal(this, 'confirmation', {
          width: 'medium',
          titleText: 'Sorry, an error occurred',
          bodyText: 'This file cannot be displayed.',
          actionButtonText: 'Ok',
        });
      })
      .finally(() => {
        this.set('state.isLoading', false);
      });
  },

  /**
   * @param {String} url The image URL.
   * @returns {Promise<HTMLImageElement>} The image element.
   */
  _createImageElement(url) {
    const image = document.createElement('img');
    image.classList.add(`${CLASS_BASE}__image`);

    return new Promise((resolve, reject) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', (event) => reject(event.target.error), { once: true });
      image.src = url;
    })
      .then(() => image);
  },

  /**
   * @param {String} url The video URL.
   * @returns {Promise<HTMLVideoElement>} The video element.
   */
  _createVideoElement(url) {
    const video = document.createElement('video');
    video.classList.add(`${CLASS_BASE}__video`);
    video.playsInline = true;
    video.autoplay = true;
    video.controls = true;

    return new Promise((resolve, reject) => {
      video.addEventListener('loadedmetadata', () => resolve(), { once: true });
      video.addEventListener('error', (event) => reject(event.target.error), { once: true });
      video.src = url;
    })
      .then(() => video);
  },

  /**
   * @type {Ember.String.htmlSafe} The `style` value for the modal which dictates its size.
   */
  _modalStyles: computed('state.assetWidth', 'state.assetHeight', function () {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const assetWidth = this.get('state.assetWidth');
    const assetHeight = this.get('state.assetHeight');

    const reservedLeftSpace = windowWidth * RESERVED_SPACE_PCT;
    const reservedRightSpace = reservedLeftSpace;
    const availableWidth = windowWidth - reservedLeftSpace - reservedRightSpace - (PADDING_PX * 2);

    const reservedTopSpace = windowHeight * RESERVED_SPACE_PCT + EXTRA_RESERVED_VERTICAL_SPACE_PX;
    const reservedBottomSpace = reservedTopSpace;
    const availableHeight = windowHeight - reservedTopSpace - reservedBottomSpace - (PADDING_PX * 2);

    // The largest scale factor that doesn't have the asset break outside the designated boundary.
    const maxScaleFactor = Math.min(availableWidth / assetWidth, availableHeight / assetHeight);

    // The scale factor capped at 1 to prevent the asset from being scaled up.
    const scaleFactor = Math.min(maxScaleFactor, 1);

    const modalWidth = (assetWidth * scaleFactor) + (PADDING_PX * 2);
    const modalHeight = (assetHeight * scaleFactor) + (PADDING_PX * 2);

    return htmlSafe(`
      padding: ${PADDING_PX}px;
      width: ${modalWidth}px !important;
      height: ${modalHeight}px !important;
    `);
  }),

  actions: {
    onClickClose() {
      closeModal(this);
    },

    onClickDownload() {
      this.get('model.handleClickDownload')();
    },
  },
});
